import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getPixelsSummary } from '../../services/pixelsService';
import './billing.css';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

type ConfirmationState = 'waiting' | 'confirmed' | 'timeout';

/**
 * Landing page for Stripe Checkout success redirects
 * (`/billing/success?session_id=...`). The redirect is never trusted —
 * confirmation waits until `getPixelsSummary` reports a risen `available`
 * balance (30s timeout), then links home.
 */
const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState<ConfirmationState>('waiting');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setState('timeout');
      return;
    }
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (next: ConfirmationState) => {
      if (cancelled) return;
      if (intervalId !== undefined) clearInterval(intervalId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      setState(next);
    };

    void (async () => {
      let baseline: number | null = null;
      try {
        baseline = (await getPixelsSummary(token)).available;
      } catch {
        baseline = null;
      }
      if (cancelled) return;

      const check = async () => {
        try {
          const current = (await getPixelsSummary(token)).available;
          if (cancelled) return;
          if (current === null) return;
          if (baseline === null || current > baseline) {
            finish('confirmed');
          }
        } catch {
          /* transient failure — keep polling until the timeout */
        }
      };

      await check();
      if (cancelled) return;
      intervalId = setInterval(() => void check(), POLL_INTERVAL_MS);
      timeoutId = setTimeout(() => finish('timeout'), POLL_TIMEOUT_MS);
    })();

    return () => {
      cancelled = true;
      if (intervalId !== undefined) clearInterval(intervalId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="billing-page">
      <section className="billing-card" aria-label="Payment confirmation">
        {state === 'waiting' && (
          <>
            <h1>Confirming your payment…</h1>
            <p className="billing-status">
              {sessionId
                ? `Checking session ${sessionId} — your pixels will appear shortly.`
                : 'Your pixels will appear shortly.'}
            </p>
          </>
        )}
        {state === 'confirmed' && (
          <>
            <h1>Payment successful</h1>
            <p className="billing-status">
              Your pixels are now available. Happy creating!
            </p>
            <Link className="billing-primary billing-link" to="/">
              Back to home
            </Link>
          </>
        )}
        {state === 'timeout' && (
          <>
            <h1>Payment received</h1>
            <p className="billing-status">
              We couldn&apos;t confirm your new balance yet — it can take a
              moment to arrive. Check billing or head home.
            </p>
            <div className="billing-actions">
              <Link className="billing-primary billing-link" to="/">
                Back to home
              </Link>
              <Link className="billing-secondary billing-link" to="/billing">
                Back to billing
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default SuccessPage;
